/**
 * @file Handles shopping-cart
 * @author diego.morales@notch-interactive.com
 *
 * @module modules/m11-2-shopping-cart
 *
 *
 */

import once from '../helpers/once';
import observer from '../libs/observer';
import cartItem from '../components/cart-item';
import pubsub from '../libs/pubsub';

let defaults = {
    cartAttr: 'data-cart',
    cartTotalAttr: 'data-cart-total',
    cartItemAttr: 'data-cart-item',
    cartItemTemplateId: 'cart-item-template',
    cartItemsContainerAttr: 'data-items-container',
    cartItemDeleteAttr: 'data-delete',
    cartCheckoutAttr: 'data-cart-checkout',
    fadeOutClass: 'fade-out',
    isEmptyClass: 'is-empty',
    shopOverlayClass: 'm-shop-overlay'
};

const shoppingCart = (el, options) => {
    let instance = {},
        settings = Object.assign({}, defaults, options),
        cartTotal,
        cartItemTemplateHTML,
        cartItemsContainer,
        cartItemCollection = {},
        cartItemFactories = [],
        total = 0,
        totalAmount = 0,
        btnSubmit,

        calculateTotal = (cartItemObj) => {
            total = 0;
            totalAmount = 0;

            cartItemCollection = Object.assign({}, cartItemCollection, cartItemObj);

            for (let c in cartItemCollection) {
                if (cartItemCollection.hasOwnProperty(c)) {
                    total += cartItemCollection[c].subtotal || (cartItemCollection[c].amount * cartItemCollection[c].price);
                    totalAmount += cartItemCollection[c].amount;
                }
            }

            cartTotal.innerHTML = total;

            if (el.closest('.' + settings.shopOverlayClass)) {
                if (total === 0) {
                    el.closest('.' + settings.shopOverlayClass).classList.add(settings.isEmptyClass);
                }else {
                    el.closest('.' + settings.shopOverlayClass).classList.remove(settings.isEmptyClass);
                }
            } else {
                if (total === 0) {
                    el.classList.add(settings.isEmptyClass);
                } else {
                    el.classList.remove(settings.isEmptyClass);
                }
            }

            // pubsub.trigger('shop.totalCalculated', {
            //     total: total,
            //     totalAmount: totalAmount,
            //     cartItems: cartItemCollection
            // });

            instance.trigger('totalCalculated', {
                total: total,
                totalAmount: totalAmount,
                cartItems: cartItemCollection
            });
        },

        renderCartItems = () => {
            let html = '',
                renderItem = (id, data) => {
                    let tmp = document.createElement('div');

                    tmp.innerHTML = cartItemTemplateHTML;

                    tmp.querySelector('[data-cart-item]').setAttribute('data-cart-item', id);
                    tmp.querySelector('[data-price]').setAttribute('data-price', data.price);
                    tmp.querySelector('[data-input]').setAttribute('value', data.amount);   // Element of number-stepper
                    tmp.querySelector('[data-title]').innerHTML = data.title;
                    tmp.querySelector('[data-description]').innerHTML = data.description;

                    // set hidden input fields with values so they can be serialized
                    tmp.innerHTML += `<input type="hidden" name="item-${id}-price" value="${data.price}">`;
                    tmp.innerHTML += `<input type="hidden" name="item-${id}-amount" value="${data.amount}">`;
                    tmp.innerHTML += `<input type="hidden" name="item-${id}-name" value="${data.title}">`;

                    return tmp.innerHTML;
                };

            cartItemCollection = Object.assign({}, cartItemCollection);

            for (let c in cartItemCollection) {
                if (cartItemCollection.hasOwnProperty(c)) {
                    html += renderItem(c, cartItemCollection[c]);
                }
            }

            // Replace content
            cartItemsContainer.innerHTML = html;

            // Update factories
            cartItemFactories = [];
            cartItemsContainer.querySelectorAll(`[${settings.cartItemAttr}]`)
                .forEach((item) => {
                    let ci = cartItem(item);

                    ci.on('subtotalChanged', (cartItemObj) => {
                        calculateTotal(cartItemObj);

                        instance.trigger('subtotalChanged', {
                            total: total,
                            totalAmount: totalAmount,
                            cartItems: cartItemCollection
                        });
                    });

                    cartItemFactories.push(ci);
                });
        },

        removeCartItem = (item) => {
            once(item, 'transitionend', () => {
                delete cartItemCollection[item.getAttribute(settings.cartItemAttr)];

                renderCartItems();
                calculateTotal();

                instance.trigger('itemDeleted', {
                    total: total,
                    totalAmount: totalAmount,
                    cartItems: cartItemCollection
                });
            });

            item.classList.add(settings.fadeOutClass);
        },

        addItem = (itemObj) => {
            let key = Object.keys(itemObj)[0];

            if (cartItemCollection.hasOwnProperty(key)) {
                cartItemCollection[key].amount += itemObj[key].amount;
                cartItemCollection[key].price = itemObj[key].price;
                cartItemCollection[key].title = itemObj[key].title;
                cartItemCollection[key].description = itemObj[key].description;
            } else {
                cartItemCollection[key] = Object.assign({}, itemObj[key]);
            }

            renderCartItems();
            calculateTotal();
        },

        bindEvents = () => {
            cartItemsContainer.addEventListener('click', (e) => {
                if (e.target.closest(`[${settings.cartItemDeleteAttr}]`)) {
                    removeCartItem(e.target.closest(`[${settings.cartItemAttr}]`));
                    e.preventDefault();
                }
            });
        };

    if (!(el instanceof HTMLElement)) {
        return;
    }

    // Add composites
    Object.assign(instance, observer());

    cartItemTemplateHTML = document.getElementById(settings.cartItemTemplateId).innerHTML;
    cartItemsContainer = el.querySelector(`[${settings.cartItemsContainerAttr}]`);
    cartTotal = el.querySelector(`[${settings.cartTotalAttr}]`) || document.querySelector(`.overlay [${settings.cartTotalAttr}]`);  // This element can be somewhere in an overlay

    // Check if submit button is in form
    if (!el.querySelector(`[${settings.cartCheckoutAttr}]`)) {

        // Shopping-Cart is most probably in an overlay (and because of layout submit is outside of the form.
        btnSubmit = el.closest('.overlay').querySelector(`[${settings.cartCheckoutAttr}] button[type="submit"]`);
        btnSubmit.addEventListener('click', () => el.submit());
    }

    // getCartItems();
    renderCartItems();
    calculateTotal();
    bindEvents();

    // Public methods
    instance.addItem = addItem;

    instance.getTotalAmount = () => totalAmount;

    instance.getTotal = () => total;

    instance.applyItems = (itemsCollection) => {

        // Apply Items to shopping-carts. With this json trick no reference will be copied.
        cartItemCollection = JSON.parse(JSON.stringify(itemsCollection));

        renderCartItems();
        calculateTotal();
    };

    // Subscriptions
    pubsub.on('shoppingCart.addItem', (data) => {
        addItem(data);
    });

    return instance;
};

export default shoppingCart;

// Export module to window
window.DS = window.DS || {};
window.DS.shoppingCart = shoppingCart;
